/**
 * Declare a mounted component's feed-filter needs for as long as it is
 * mounted (consumer feed filters, Stage C). Mirrors the `setOrderbookNeeds`
 * effect every view already carries.
 *
 *   useFeedNeeds('game-lines:mlb', isMlb ? { classes: ['player-prop'] } : null);
 *   useFeedNeeds('value-dashboard', { sports: allSports ? 'all' : selected });
 *
 * `needs` is compared by value (a stable key), so callers may pass fresh
 * object literals every render without re-publishing. Passing `null` clears
 * the declaration (same as unmounting).
 *
 * DECLARING AND CLEARING ARE SEPARATE EFFECTS ON PURPOSE (2026-09-05). A
 * value change must never clear this source first. `declareFeedNeeds`
 * REPLACES the source's entry, so re-declaring needs no clear — and must not
 * do one: React runs an effect's cleanup before its next run, and the
 * registry publishes on every change, so a clear/re-declare pair publishes a
 * TRANSIENT union with this source missing before publishing the real one.
 *
 * That transient is not cosmetic. `useHydra`'s floor-cadence negotiation
 * reads the published union, and a `/game/*` page dropping out of it leaves
 * the viewed set EMPTY — so the negotiator finds nothing sole-sourced and
 * asks hc1 for the 1000 ms standby, then asks again for 250 the moment the
 * real declaration lands. Each flip re-sends a complete markets filter. The
 * two-phase declaration in `useRouteSportFeedNeeds` (sport alone, then sport
 * + wire keys once rows land) changes this key routinely, so on a busy
 * Saturday one tab did that ~8x/minute and each flip cost hydracore a full
 * replacement snapshot (~43k rows). hc1 wedged on 2026-09-05.
 */
import { useEffect } from 'react';
import { declareFeedNeeds } from '../stores/feedNeeds';
import type { FeedNeeds } from '../workers/feedFilter';

/** Value key for a declaration — every axis, so a change on any one of them
 *  re-publishes. Exported for the ticker-scoped hook's change detection. */
export function feedNeedsKey(needs: FeedNeeds | null): string {
  if (needs == null) return '';
  const list = (v?: Iterable<string>): string[] => (v ? [...v].sort() : []);
  return JSON.stringify({
    k: needs.tickers === undefined ? '*' : list(needs.tickers),
    c: list(needs.classes),
    t: list(needs.marketTypes),
    s: needs.sports === undefined || needs.sports === 'all' ? '*' : list(needs.sports),
    // Every axis must be in the key. Omitting one means a declaration that
    // changes ONLY that axis never re-publishes — the games axis was invisible
    // this way until a two-phase test caught it.
    g: needs.games === undefined || needs.games === 'all' ? '*' : list(needs.games),
  });
}

export function useFeedNeeds(sourceId: string, needs: FeedNeeds | null): void {
  const key = feedNeedsKey(needs);
  // Value changes: declare over the top, no clear (see the header).
  useEffect(() => {
    declareFeedNeeds(sourceId, needs);
    // `needs` is folded into `key` by value; the object identity is irrelevant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, key]);
  // Clearing is an UNMOUNT (or source-swap) concern only — never a value
  // change, so this effect deliberately does not depend on `key`.
  useEffect(() => () => declareFeedNeeds(sourceId, null), [sourceId]);
}
